import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { ArticleInlineCta } from "../components/ArticleInlineCta";
import { PageState } from "../components/PageState";
import { SITE_URL } from "../lib/config";
import { getArticle, getCachedArticle, type Post } from "../lib/articles";
import { formatDate } from "../lib/format";
import { usePageMetadata } from "../lib/metadata";

export function ArticlePage() {
  const { slug = "" } = useParams();
  const [articleState, setArticleState] = useState<{ slug: string; article: Post | null }>(() => ({
    slug,
    article: getCachedArticle(slug),
  }));
  const [cached, setCached] = useState(false);
  const [failedSlug, setFailedSlug] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const article = articleState.slug === slug ? articleState.article : null;

  usePageMetadata({
    title: article?.metaTitle || "Loading guide — Emails Unsubscriber",
    description: article?.metaDescription || "A practical Emails Unsubscriber inbox-cleanup guide.",
    path: `/articles/${slug}`,
    type: "article",
  });

  useEffect(() => {
    const controller = new AbortController();
    const savedArticle = getCachedArticle(slug);
    setArticleState({ slug, article: savedArticle });
    setCached(false);
    setFailedSlug(null);
    getArticle(slug, controller.signal)
      .then((result) => {
        setArticleState({ slug, article: result.data });
        setCached(result.cached);
      })
      .catch(() => {
        if (!controller.signal.aborted && !savedArticle) setFailedSlug(slug);
      });
    return () => controller.abort();
  }, [attempt, slug]);

  useEffect(() => {
    if (!article) return;
    const id = "unsub-article-structured-data";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.metaDescription,
      datePublished: article.publishedAt,
      dateModified: article.updatedAt,
      mainEntityOfPage: `${SITE_URL}/articles/${article.slug}`,
      publisher: { "@type": "Organization", name: "Emails Unsubscriber", url: SITE_URL },
      keywords: article.seoKeywords.join(", "),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [article]);

  if (failedSlug === slug) {
    return <PageState title="This guide is temporarily unavailable." detail="Try again in a moment, or open Emails Unsubscriber to start cleaning up your inbox." action={retry} />;
  }

  if (!article) {
    return <ArticleLoading />;
  }

  const content = article.content.replace(/^#\s+.*(?:\r?\n)+/, "");
  const firstParagraphBreak = content.search(/\r?\n\s*\r?\n/);
  const openingParagraph = firstParagraphBreak >= 0 ? content.slice(0, firstParagraphBreak).trim() : content;
  const continuation = firstParagraphBreak >= 0 ? content.slice(firstParagraphBreak).trim() : "";

  return (
    <>
      <article className="unsub-article">
        <div className="unsub-article-shell">
          <Link className="unsub-article-back" to="/articles">← All inbox guides</Link>
          {cached ? <p className="unsub-cache-note" role="status">Showing the most recent saved copy.</p> : null}
          <header className="unsub-article-header">
            <div className="unsub-guide-meta">
              <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>
              {article.readTime ? <span>{article.readTime} min read</span> : null}
            </div>
            <h1>{article.title}</h1>
            {article.excerpt ? <p>{article.excerpt}</p> : null}
          </header>
          <div className="unsub-article-prose">
            {openingParagraph ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{openingParagraph}</ReactMarkdown> : null}
            <ArticleInlineCta />
            {continuation ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{continuation}</ReactMarkdown> : null}
          </div>
        </div>
      </article>
    </>
  );
}

function ArticleLoading() {
  return (
    <article className="unsub-article" aria-busy="true" aria-label="Loading guide">
      <div className="unsub-article-shell">
        <Link className="unsub-article-back" to="/articles">← All inbox guides</Link>
        <div className="unsub-article-loading" aria-hidden="true">
          <div className="unsub-article-loading-meta" />
          <div className="unsub-article-loading-title"><span /><span /></div>
          <div className="unsub-article-loading-copy"><span /><span /><span /></div>
        </div>
      </div>
    </article>
  );
}
